import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { ROUTE_OPERATIONAL, ROUTE_ORDERS, ROUTE_PAYABLE, ROUTE_RECEIVABLE, buildNavPathTrader } from '@klear/constants'
import { Wizard } from '@klear/ui/wizard/wizard'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { enhanceReturnUrl, returnUrlHasPathSegment, useReturnUrlExit } from '@klear/ui/wizard/return-url'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import { OrderFlow } from '@/app/order/workflow/order-step-group'
import { returnUrlWithOrderId } from '@/app/workflow/return-url-host'
import { STEP_ID as PAYABLE_STEP_ID } from '@klear/payable/constants'
import { STEP_ID as RECEIVABLE_STEP_ID } from '@/app/receivable/workflow/receivable-steps'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function OrderCreateWizardPage() {
  useDocumentTitle('New Order')
  const { traderId } = useParams<{ traderId?: string }>()
  const tid = toInteger(traderId)
  const ordersList = buildNavPathTrader(toString(tid), ROUTE_ORDERS)
  const { exit, exitAfterSave } = useReturnUrlExit(ordersList)
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const [dirty, setDirty] = useState(false)
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: dirty })
  const guardedExit = useMemo(() => guardExit(exit), [guardExit, exit])

  const onComplete = useCallback(
    ({ orderId }: { orderId: number }) => {
      exitAfterSave((ret) => {
        if (returnUrlHasPathSegment(ret, ROUTE_OPERATIONAL)) {
          return enhanceReturnUrl(ret, { query: { orderId: toString(orderId) } })
        }
        let next = returnUrlWithOrderId(ret, orderId, ROUTE_PAYABLE)
        if (next === ret) {
          next = returnUrlWithOrderId(ret, orderId, ROUTE_RECEIVABLE)
        }
        if (next === ret) return ret
        const hash = returnUrlHasPathSegment(next, ROUTE_PAYABLE)
          ? PAYABLE_STEP_ID.OPERATIONAL
          : RECEIVABLE_STEP_ID.PURCHASE_ORDER
        return enhanceReturnUrl(next, { hash })
      })
    },
    [exitAfterSave]
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {LeaveConfirmDialog}
      <Wizard
        name="Order"
        subtitle={nav?.steps.find((s) => s.id === nav.activeStepId)?.label ?? 'Create'}
        steps={nav?.steps ?? []}
        activeStepId={nav?.activeStepId ?? ''}
        onStepSelect={nav?.onStepSelect}
        isStepDisabled={nav?.isStepDisabled}
        onBack={guardedExit}
        backLabel="Exit"
      >
        <OrderFlow
          traderId={tid}
          cancelOnFirstStep
          onNavStateChange={setNav}
          onComplete={onComplete}
          onCancel={guardedExit}
          onDirtyChange={setDirty}
        />
      </Wizard>
    </div>
  )
}
